import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Bell, BellOff, Send, Users, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  isNotificationSupported,
  getNotificationPermissionStatus 
} from '../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Push notification subscription component for users
export const PushNotificationToggle = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');

  useEffect(() => {
    // Check current permission status
    setPermissionStatus(getNotificationPermissionStatus());
    
    // Check subscription status
    checkSubscriptionStatus();
    
    // Listen for foreground messages
    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast for foreground notifications
      toast.info(payload.notification?.title || 'Nuova notifica', {
        description: payload.notification?.body
      });
    });
    
    return () => unsubscribe();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSubscribed(response.data.subscribed);
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleToggleSubscription = async () => {
    if (!isNotificationSupported()) {
      toast.error('Il tuo browser non supporta le notifiche push');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isSubscribed) {
        // Unsubscribe
        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) {
          await axios.delete(`${API}/push/unsubscribe?fcm_token=${fcmToken}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.removeItem('fcm_token');
        }
        setIsSubscribed(false);
        toast.success('Notifiche disattivate');
      } else {
        // Subscribe
        const fcmToken = await requestNotificationPermission();
        
        if (fcmToken) {
          await axios.post(`${API}/push/subscribe`, {
            fcm_token: fcmToken,
            device_info: navigator.userAgent
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('fcm_token', fcmToken);
          setIsSubscribed(true);
          setPermissionStatus('granted');
          toast.success('Notifiche attivate! Riceverai aggiornamenti su eventi, certificati e news.');
        } else {
          toast.error('Permesso notifiche negato. Abilita le notifiche nelle impostazioni del browser.');
          setPermissionStatus(getNotificationPermissionStatus());
        }
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Errore nella gestione delle notifiche');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isNotificationSupported()) {
    return null;
  }

  return (
    <div className="flex items-center space-x-3">
      <Button
        onClick={handleToggleSubscription}
        variant={isSubscribed ? "default" : "outline"}
        size="sm"
        disabled={isLoading}
        className={isSubscribed ? "bg-green-500 hover:bg-green-600" : ""}
        data-testid="push-notification-toggle"
      >
        {isLoading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
        ) : isSubscribed ? (
          <Bell className="w-4 h-4 mr-2" />
        ) : (
          <BellOff className="w-4 h-4 mr-2" />
        )}
        {isSubscribed ? 'Notifiche Attive' : 'Attiva Notifiche'}
      </Button>
      {permissionStatus === 'denied' && (
        <span className="text-xs text-red-500">
          Notifiche bloccate nel browser
        </span>
      )}
    </div>
  );
};

// Admin component for sending push notifications
export const AdminPushNotifications = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetAudience, setTargetAudience] = useState('tutti');
  const [notificationType, setNotificationType] = useState('news');
  const [isSending, setIsSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/push/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }

    setIsSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/push/send`, {
        title,
        body,
        target_audience: targetAudience,
        notification_type: notificationType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      if (data.success_count > 0) {
        toast.success(
          `Notifica inviata! ${data.success_count} successi, ${data.failure_count} fallimenti`
        );
      } else if (data.total_tokens === 0) {
        toast.warning('Nessun utente ha le notifiche attive per il pubblico selezionato');
      } else if (data.failure_count > 0 && data.success_count === 0) {
        toast.error(
          `Invio fallito: tutti i ${data.failure_count} token erano invalidi/scaduti e sono stati rimossi. Gli utenti dovranno riattivare le notifiche.`
        );
      } else {
        toast.info('Notifica elaborata');
      }
      
      // Clear form
      setTitle('');
      setBody('');
      
      // Refresh history
      fetchHistory();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Errore nell\'invio della notifica');
    } finally {
      setIsSending(false);
    }
  };

  const getAudienceLabel = (audience) => {
    const labels = {
      'tutti': 'Tutti gli utenti',
      'teacher': 'Solo Teacher',
      'teacher_formatore': 'Teacher + Formatori',
      'formatore': 'Solo Formatori',
      'atleta': 'Solo Atleti',
      'admin': 'Solo Admin'
    };
    return labels[audience] || audience;
  };

  const getTypeIcon = (type) => {
    const icons = {
      'news': '📢',
      'evento': '🎉',
      'certificato': '🏆',
      'profilo': '📋'
    };
    return icons[type] || '📢';
  };

  return (
    <div className="space-y-6">
      {/* Send Notification Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Send className="w-5 h-5" />
            <span>Invia Notifica Push</span>
          </CardTitle>
          <CardDescription>
            Invia notifiche push a gruppi specifici di utenti
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notification-title">Titolo *</Label>
              <Input
                id="notification-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="es. Nuovo Evento Disponibile!"
                maxLength={100}
                data-testid="notification-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="target-audience">Destinatari *</Label>
              <Select value={targetAudience} onValueChange={setTargetAudience}>
                <SelectTrigger data-testid="target-audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      TUTTI
                    </div>
                  </SelectItem>
                  <SelectItem value="teacher">TEACHER</SelectItem>
                  <SelectItem value="teacher_formatore">TEACHER + FORMATORE</SelectItem>
                  <SelectItem value="formatore">FORMATORE</SelectItem>
                  <SelectItem value="atleta">ATLETA</SelectItem>
                  <SelectItem value="admin">ADMIN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notification-body">Messaggio *</Label>
            <Textarea
              id="notification-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Scrivi il messaggio della notifica..."
              rows={3}
              maxLength={500}
              data-testid="notification-body-input"
            />
            <p className="text-xs text-gray-500">{body.length}/500 caratteri</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notification-type">Tipo Notifica</Label>
            <Select value={notificationType} onValueChange={setNotificationType}>
              <SelectTrigger data-testid="notification-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="news">📢 News Generale</SelectItem>
                <SelectItem value="evento">🎉 Evento</SelectItem>
                <SelectItem value="certificato">🏆 Certificato</SelectItem>
                <SelectItem value="profilo">📋 Aggiornamento Profilo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSendNotification}
            disabled={isSending || !title.trim() || !body.trim()}
            className="w-full"
            data-testid="send-notification-button"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Invia Notifica
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Notification History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="w-5 h-5" />
            <span>Storico Notifiche</span>
          </CardTitle>
          <CardDescription>
            Ultime notifiche inviate
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Nessuna notifica inviata
            </p>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-lg">{getTypeIcon(item.notification_type)}</span>
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{item.body}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline">
                          {getAudienceLabel(item.target_audience)}
                        </Badge>
                        <span className="text-gray-500">
                          {new Date(item.sent_at).toLocaleString('it-IT')}
                        </span>
                        <span className="text-gray-500">
                          da {item.sent_by_name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-sm">{item.success_count}</span>
                      </div>
                      {item.failure_count > 0 && (
                        <div className="flex items-center text-red-600">
                          <XCircle className="w-4 h-4 mr-1" />
                          <span className="text-sm">{item.failure_count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PushNotificationToggle;
